import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, X } from "lucide-react";

// Campo de busca com autocomplete (substitui selects). items: [{id, label, sub?}]
export const Autocomplete = ({ items = [], value, onChange, placeholder = "Digite para buscar...", testid }) => {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const boxRef = useRef(null);

  const selected = useMemo(() => items.find((i) => i.id === value), [items, value]);

  // Sincroniza o texto exibido quando um valor é selecionado externamente
  useEffect(() => {
    if (selected) setQ(selected.label);
    else if (!value) setQ("");
  }, [selected, value]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t || (selected && q === selected.label)) return items.slice(0, 50);
    return items.filter((i) => i.label.toLowerCase().includes(t) || (i.sub || "").toLowerCase().includes(t)).slice(0, 50);
  }, [q, items, selected]);

  const pick = (item) => { onChange(item.id); setQ(item.label); setOpen(false); };
  const clear = () => { onChange(""); setQ(""); setOpen(true); };

  const onKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (filtered[hi]) pick(filtered[hi]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div className="relative" ref={boxRef}>
      <div className="flex items-center gap-2 px-3.5 rounded-lg border border-slate-200 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100 transition bg-white">
        <Search className="w-4 h-4 text-slate-400 shrink-0" />
        <input
          value={q}
          data-testid={testid}
          onChange={(e) => { setQ(e.target.value); setOpen(true); setHi(0); if (value) onChange(""); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder={placeholder}
          className="flex-1 py-2.5 bg-transparent outline-none text-sm text-slate-700"
          autoComplete="off"
        />
        {q && <button type="button" onClick={clear} className="text-slate-300 hover:text-slate-500 shrink-0"><X className="w-4 h-4" /></button>}
      </div>
      {open && filtered.length > 0 && (
        <div className="absolute z-30 mt-1 w-full max-h-60 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg" data-testid={testid ? `${testid}-options` : undefined}>
          {filtered.map((item, idx) => (
            <button
              key={item.id}
              type="button"
              onMouseEnter={() => setHi(idx)}
              onClick={() => pick(item)}
              data-testid={testid ? `${testid}-opt-${item.id}` : undefined}
              className={`w-full text-left px-3.5 py-2 text-sm transition-colors ${idx === hi ? "bg-blue-50 text-blue-700" : "text-slate-700 hover:bg-slate-50"}`}
            >
              <span className="font-medium">{item.label}</span>
              {item.sub && <span className="text-slate-400 ml-2 text-xs">{item.sub}</span>}
            </button>
          ))}
        </div>
      )}
      {open && filtered.length === 0 && q.trim() && (
        <div className="absolute z-30 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg px-3.5 py-3 text-sm text-slate-400">
          Nenhum resultado para "{q}".
        </div>
      )}
    </div>
  );
};

export default Autocomplete;
